import { FC, FormEvent, useEffect, useState } from 'react'
import { useSendPasswordResetEmail, useSignInWithEmailAndPassword } from 'react-firebase-hooks/auth'
import { useTranslation } from 'react-i18next'
import { Location, Navigate } from 'react-router'
import LockIcon from '@mui/icons-material/Lock'
import PersonIcon from '@mui/icons-material/Person'
import { Alert, AlertTitle, Box, Button, Stack } from '@mui/material'
import {
  AuthErrorCodes,
  browserLocalPersistence,
  getMultiFactorResolver,
  MultiFactorError,
  MultiFactorResolver,
  setPersistence,
} from 'firebase/auth'

import { fbAuth, useAuth } from '@/modules/authentication'
import { IconField } from '@/modules/authentication/IconField'
import LoginFormMFA from '@/modules/authentication/LoginFormMFA'
import { useLocationState } from '@/modules/urlRouting/hooks'
import { PATHS } from '@/modules/urlRouting/paths'

fbAuth.useDeviceLanguage()

const toHome = (from: Location<any> | undefined) => {
  const navigateUserTo =
    from?.pathname && from?.pathname !== PATHS.ROOT ? `${from.pathname}${from.search}` : PATHS.ROOT
  return navigateUserTo
}

const LoginFormPassword: FC<{ initialEmail: string }> = ({ initialEmail }) => {
  const { t } = useTranslation()
  const { data: currentUser } = useAuth()

  const [signInWithEmailAndPassword, user, authLoading, authError] =
    useSignInWithEmailAndPassword(fbAuth)

  const [sendPasswordResetEmail, resetSending, resetError] = useSendPasswordResetEmail(fbAuth)
  const [passwordResetSent, setPasswordResetSent] = useState(false)

  const [email, setEmail] = useState(initialEmail || '')
  const [password, setPassword] = useState('')

  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>()

  const { from } = useLocationState()

  useEffect(() => {
    if (authError?.code === AuthErrorCodes.MFA_REQUIRED)
      setMfaResolver(getMultiFactorResolver(fbAuth, authError as MultiFactorError))
  }, [authError])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setPasswordResetSent(false)

    await setPersistence(fbAuth, browserLocalPersistence)
    signInWithEmailAndPassword(email, password)
  }

  const passwordReset = async () => {
    await sendPasswordResetEmail(email)
    setPasswordResetSent(true)
  }

  if (user && currentUser) {
    return <Navigate to={toHome(from)} replace state={{ from: undefined }} />
  } else if (!authLoading && !resetSending && mfaResolver) {
    return <LoginFormMFA mfaResolver={mfaResolver} />
  }

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2}>
        {!authLoading &&
          !resetSending &&
          (authError || resetError) &&
          authError?.code !== AuthErrorCodes.MFA_REQUIRED && (
            <Alert severity="error">
              <AlertTitle>{t('login.loginError', 'Login Error')}</AlertTitle>
              {authError && <div>{t(`login.authError_${authError?.code}`, authError.message)}</div>}
              {resetError && <div>{t('login.resetError', 'Error sending reset email.')}</div>}
            </Alert>
          )}

        {passwordResetSent && !resetSending && !resetError && (
          <Alert severity="success">
            <AlertTitle>{t('login.resetEmailHeader', 'Password Reset Email Sent')}</AlertTitle>
            {t(
              'login.resetEmailMessage',
              'An email with a link to reset your password has been sent to {{user_email}}.',
              { user_email: email || 'your email' }
            )}
          </Alert>
        )}

        <IconField
          name="email"
          value={email}
          placeholder={t('login.emailAddress', 'EMAIL ADDRESS')}
          onChange={({ target: { value } }) => setEmail(value)}
          icon={<PersonIcon color="action" />}
        />
        <IconField
          name="password"
          type="password"
          value={password}
          placeholder={t('login.password', 'PASSWORD')}
          onChange={({ target: { value } }) => setPassword(value)}
          icon={<LockIcon color="action" />}
        />
        <Button type="submit" variant="contained" fullWidth size="large">
          {t('common.login', 'Login')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          type="button"
          onClick={passwordReset}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('login.resetPassword', 'Reset Password')}
        </Button>
      </Stack>
    </Box>
  )
}

export default LoginFormPassword
